import {
  Box,
  Chip,
  Divider,
  Grid,
  Link,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import TableDesign from "../../../components/common/table/TableDesign";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  getAllLeadLogListAction,
  setAddLeadLogAction,
  setUpdateLeadAction,
} from "../../../store/reducers/leadSlice";
import { setCustomerResendCustomerResponse } from "../../../store/reducers/customerSlice";
import dayjs from "../../../utils/dayjsConfig";
import Popup from "../../../components/common/model/Popup";
import {
  sendLogEmailAction,
  setLogEmailResponseDataAction,
} from "../../../store/reducers/templateSlice";

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  leadId?: string;
}

interface LeadLogListProps {
  setLeadId?: any;
  leadId?: any;
}

const LeadLogList: React.FC<LeadLogListProps> = ({ leadId }) => {
  const dispatch = useDispatch();
  const { logs, leadLogPagination, isUpdateLeadSuccess, isAddLeadLogSuccess } =
    useSelector((state: any) => state.lead);
  const { userProfile } = useSelector((state: any) => state?.user);
  const { logEmailResponseData } = useSelector((state: any) => state?.template);
  const { isResetCustomerLinkResponse } =
    useSelector((state: any) => state.customer);
  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
    leadId: leadId,
  });

  const [isLoader] = useState(false);
  const [isEmailDetailModalOpen, setIsEmailDetailModalOpen] = useState(false);

  const getActionParts = (action: string) => {
    const words = (action || "").trim().split(/\s+/).filter(Boolean);
    const clickableText = words.slice(0, 2).join(" ");
    const remainingText = words.slice(2).join(" ");
    return { clickableText, remainingText };
  };

  const handleResendEmail = (logItem: any) => {
    if (!logItem?.sent_email_id) return;

    setIsEmailDetailModalOpen(true);
    dispatch(setLogEmailResponseDataAction(null));
    dispatch(
      sendLogEmailAction({
        id: logItem?.sent_email_id,
      }),
    );
  };

  const closeEmailDetailModal = () => {
    setIsEmailDetailModalOpen(false);
    dispatch(setLogEmailResponseDataAction(null));
  };

  const toRecipients = logEmailResponseData?.To || [];

  useEffect(() => {
    if (leadId) {
      setFilterOption((prev) => ({ ...prev, leadId }));
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId && filterOption?.leadId) {
      dispatch(getAllLeadLogListAction(filterOption));
    }
    setUpdateLeadAction(false);
    dispatch(setAddLeadLogAction(false));
  }, [
    dispatch,
    leadId,
    filterOption,
    isUpdateLeadSuccess,
    isAddLeadLogSuccess,
  ]);

  useEffect(() => {
    if (isResetCustomerLinkResponse) {
      dispatch(getAllLeadLogListAction(filterOption));
      dispatch(setCustomerResendCustomerResponse(false));
    }
  }, [isResetCustomerLinkResponse, dispatch, filterOption]);

  const tableHeadings: {
    label: string;
    align: "center" | "left" | "right" | undefined;
  }[] = [
    { label: "User", align: "center" },
    { label: "Created On", align: "left" },
    { label: "Time", align: "left" },
    { label: "Action", align: "left" },
  ];

  return (
    <>
      <Popup
        title="Email Details"
        fullWidth
        maxWidth="md"
        open={isEmailDetailModalOpen}
        onHide={closeEmailDetailModal}
        popupBg="#fff"
      >
        <Box sx={{ p: 2, maxHeight: "70vh", overflowY: "auto" }}>
          {!logEmailResponseData ? (
            <Typography variant="body1" sx={{ color: "#666" }}>
              Loading email details...
            </Typography>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={12} sm={12}>
                  <Typography variant="subtitle2">Subject</Typography>
                  <Typography variant="body2">
                    {logEmailResponseData?.Subject || "N/A"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Email Body
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid #d1cfcf",
                  borderRadius: "6px",
                  backgroundColor: "#fafafa",
                  color: "#1f1f1f",
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    logEmailResponseData?.HtmlBody ||
                    logEmailResponseData?.TextBody ||
                    "N/A",
                }}
              />
            </>
          )}
        </Box>
      </Popup>

      <Grid container spacing={1} sx={{ marginLeft: "0px", marginTop: "0px" }}>
        <Grid
          item
          md={12}
          xs={12}
          sx={{ padding: "0px !important", border: "1px solid #d1cfcf" }}
        >
          <TableDesign
            tableHeadColor="#e5f4f1"
            tableHeadings={tableHeadings}
            pagination={leadLogPagination}
            isLoader={isLoader}
            stickyHeader={true}
            filterOption={filterOption}
            setFilterOption={setFilterOption}
          >
            {logs.length > 0 ? (
              logs?.map((val: any, idx: number) => (
                <TableRow
                  key={idx}
                  sx={{
                    backgroundColor: idx % 2 === 1 ? "#eeecf0" : "none",
                  }}
                >
                  <TableCell align="center" style={{ padding: "0px" }}>
                    {val?.actor_name}
                  </TableCell>
                  <TableCell>
                    {dayjs(val?.created_at).format(
                      userProfile?.dateFormat
                        ? userProfile?.dateFormat
                        : "DD/MM/YYYY",
                    )}
                  </TableCell>
                  <TableCell>
                    {dayjs(val?.created_at)
                      .tz(userProfile?.timezone || "UTC")
                      .format("HH:mm")}
                  </TableCell>
                  <TableCell>
                    {val?.sent_email_id
                      ? (() => {
                          const { clickableText, remainingText } =
                            getActionParts(val?.action || "");
                          return (
                            <>
                              <Link
                                component="button"
                                type="button"
                                underline="none"
                                onClick={() => handleResendEmail(val)}
                              >
                                {clickableText || val?.action}
                              </Link>
                              {remainingText ? ` ${remainingText}` : ""}
                            </>
                          );
                        })()
                      : val?.action}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  sx={{ textAlign: "center" }}
                  colSpan={tableHeadings?.length}
                >
                  No Data Found!
                </TableCell>
              </TableRow>
            )}
          </TableDesign>
        </Grid>
      </Grid>
    </>
  );
};
export default LeadLogList;
